import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element for the cart drawer upsell card. It adds the upsell product to the cart, or changes its quantity
 * when it is already there, then hands the fresh section HTML to the theme's own cart update pipeline so the drawer
 * (items, totals, free shipping bar and this card) re-renders.
 */
class CartUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-upsell-action]');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;
    if (this.getAttribute('aria-busy') === 'true') return;

    const quantity = Number(this.dataset.quantity) || 0;
    const action = button.dataset.upsellAction;

    if (action === 'add') {
      this.#add();
    } else if (action === 'increase') {
      this.#change(quantity + 1);
    } else if (action === 'decrease') {
      this.#change(Math.max(quantity - 1, 0));
    }
  };

  /** @returns {string} Comma separated ids of every section that renders a cart. */
  #getSectionIds() {
    const ids = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    }
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    return Array.from(ids).join(',');
  }

  #add() {
    const id = Number(this.dataset.variantId);
    if (!id) return;

    this.#request(
      Theme.routes.cart_add_url,
      { items: [{ id, quantity: 1 }] },
      /** @param {{sections?: Record<string, string>}} response */ (response) => response.sections
    );
  }

  /** @param {number} quantity */
  #change(quantity) {
    const key = this.dataset.lineKey;
    if (!key) return;

    this.#request(
      Theme.routes.cart_change_url,
      { id: key, quantity },
      /** @param {{sections?: Record<string, string>}} response */ (response) => response.sections
    );
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} payload
   * @param {(response: any) => Record<string, string> | undefined} getSections
   */
  async #request(url, payload, getSections) {
    this.setAttribute('aria-busy', 'true');

    try {
      const body = JSON.stringify({
        ...payload,
        sections: this.#getSectionIds(),
        sections_url: window.location.pathname,
      });

      const response = await fetch(url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status || data.errors) {
        console.error('[cart-upsell]', data.message || data.errors);
        return;
      }

      const sections = getSections(data);
      const sectionId = this.dataset.sectionId;
      const sectionHtml = sectionId && sections ? sections[sectionId] : undefined;

      if (!sections || !sectionHtml) {
        // No section HTML came back - fall back to a full cart re-render of every cart component.
        document.dispatchEvent(new CartUpdateEvent(data, this.id || 'cart-upsell', { source: 'cart-upsell-component' }));
        return;
      }

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const countText = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
      const itemCount = countText ? parseInt(countText, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent(data, this.id || 'cart-upsell', {
          source: 'cart-upsell-component',
          itemCount,
          sections,
        })
      );

      // The offer has been taken, so the card goes away. Liquid already leaves it out
      // of the re-rendered section, but the morph keeps this element because it is the
      // one that triggered the update, so remove it here as well.
      this.remove();
    } catch (error) {
      console.error('[cart-upsell]', error);
    } finally {
      // The component may have been morphed in place, so clear the flag on whatever is in the DOM now.
      this.removeAttribute('aria-busy');
      document.querySelectorAll('cart-upsell-component[aria-busy]').forEach((el) => el.removeAttribute('aria-busy'));
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
